import { useState, useEffect, useCallback, useMemo } from "react";
import { api, formatCurrency, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import {
  Loader2, TrendingUp, Package, BarChart3, Layers, FileDown, AlertTriangle,
} from "lucide-react";
import ChartFiltersBar from "@/components/analytics/ChartFiltersBar";
import MultiYearCompare from "@/components/analytics/MultiYearCompare";
import AdvancedCompare from "@/components/analytics/AdvancedCompare";
import HistoricalImportsManager from "@/components/analytics/HistoricalImportsManager";
import DrillDownOrdersCard from "@/components/analytics/DrillDownOrdersCard";
import ForecastActualVarianceTab from "@/components/analytics/ForecastActualVarianceTab";
import { BRAND_BLUE, BRAND_BLUE_DARK, colorFor, SERIES_COLORS } from "@/components/analytics/chartConstants";
import { computeRange, toIso, formatMonthKey } from "@/components/analytics/rangeUtils";
import { useOrg } from "@/context/OrgContext";

const TABS = ["stock", "ca", "orders", "combined", "compare", "variance", "imports"];

export const AnalyticsPage = () => {
  // ---- App config ----
  const { isOrgAdmin, isSuperAdmin } = useOrg();
  const [appConfig, setAppConfig] = useState(null);

  // ---- Reference data ----
  const [orderTypes, setOrderTypes] = useState([]);
  const [sellersList, setSellersList] = useState([]);

  // ---- Active tab persisted ----
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem("analytics_tab") || "stock");
  useEffect(() => { sessionStorage.setItem("analytics_tab", activeTab); }, [activeTab]);

  // ---- Filters (shared) ----
  const [timeRange, setTimeRange] = useState("12months");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedOrderTypeIds, setSelectedOrderTypeIds] = useState([]);
  const [selectedSellerIds, setSelectedSellerIds] = useState([]);
  const [sellerDisplayMode, setSellerDisplayMode] = useState("cumul"); // cumul | distinct

  // ---- View toggles per chart (state séparés pour éviter le bug d'onglet) ----
  const [stockMode, setStockMode] = useState("monthly"); // monthly | instant
  const [caCumulative, setCaCumulative] = useState(false);
  const [ordersCumulative, setOrdersCumulative] = useState(false);
  const [orderTypeBreakdown, setOrderTypeBreakdown] = useState(false); // stacked

  // ---- Data ----
  const [loading, setLoading] = useState(true);
  const [periodsData, setPeriodsData] = useState({ periods: [], summary: {} });
  const [stockInstant, setStockInstant] = useState(null);
  const [orderTypeData, setOrderTypeData] = useState({ periods: [], types: [] });

  // ---- Drill-down ----
  const [caBreakdown, setCaBreakdown] = useState({ period: null, loading: false, data: null });
  const [ordersBreakdown, setOrdersBreakdown] = useState({ period: null, loading: false, data: null });

  // ---- Load reference data ----
  useEffect(() => {
    api.getAppConfig().then((r) => setAppConfig(r.data)).catch(() => setAppConfig({}));
    api.getOrderTypes().then((r) => setOrderTypes(r.data)).catch(() => setOrderTypes([]));
    api.getAnalyticsSellersList().then((r) => setSellersList(r.data)).catch(() => setSellersList([]));
  }, []);

  // ---- Compute date range ----
  const dateRange = useMemo(() => {
    if (timeRange === "custom") {
      return { start: startDate, end: endDate };
    }
    return computeRange(timeRange, appConfig || {});
  }, [timeRange, startDate, endDate, appConfig]);

  // ---- Fetch data ----
  const fetchData = useCallback(async () => {
    if (!appConfig) return;
    setLoading(true);
    try {
      const params = {};
      if (dateRange.start) params.start_date = toIso(dateRange.start);
      if (dateRange.end) params.end_date = toIso(dateRange.end);
      if (selectedOrderTypeIds.length) params.order_type_ids = selectedOrderTypeIds.join(",");
      if (selectedSellerIds.length) params.seller_ids = selectedSellerIds.join(",");

      const [periodsRes, instantRes, typeRes] = await Promise.all([
        api.getAnalyticsPeriods(params),
        api.getAnalyticsStockInstant().catch(() => ({ data: null })),
        orderTypeBreakdown
          ? api.getAnalyticsByOrderType({ ...params, metric: activeTab === "ca" ? "ca" : "orders" })
          : Promise.resolve({ data: { periods: [], types: [] } }),
      ]);
      setPeriodsData(periodsRes.data);
      setStockInstant(instantRes.data);
      setOrderTypeData(typeRes.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  }, [appConfig, dateRange.start, dateRange.end, selectedOrderTypeIds, selectedSellerIds, orderTypeBreakdown, activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Cumulative transform ----
  const periodsWithCumul = useMemo(() => {
    const rows = periodsData.periods || [];
    let cumCa = 0, cumOrders = 0;
    return rows.map((r) => {
      cumCa += r.ca_amount || 0;
      cumOrders += r.orders_amount || 0;
      return {
        ...r,
        ca_cumul: Math.round(cumCa * 100) / 100,
        orders_cumul: Math.round(cumOrders * 100) / 100,
      };
    });
  }, [periodsData]);

  // ---- Drill-down handlers ----
  const handleBarClick = async (kind, payload) => {
    const period = payload?.activeLabel || payload?.payload?.period || payload?.period;
    if (!period || !period.includes("-")) return;
    const [y, m] = period.split("-").map((n) => parseInt(n, 10));
    const params = {};
    if (selectedOrderTypeIds.length) params.order_type_ids = selectedOrderTypeIds.join(",");
    if (selectedSellerIds.length) params.seller_ids = selectedSellerIds.join(",");
    if (kind === "ca") {
      setCaBreakdown({ period: { year: y, month: m }, loading: true, data: null });
      try {
        const res = await api.getAnalyticsCaDetail(y, m, params);
        setCaBreakdown({ period: { year: y, month: m }, loading: false, data: res.data });
      } catch {
        toast.error("Erreur drill-down CA");
        setCaBreakdown({ period: null, loading: false, data: null });
      }
    } else {
      setOrdersBreakdown({ period: { year: y, month: m }, loading: true, data: null });
      try {
        const res = await api.getAnalyticsOrdersDetail(y, m, params);
        setOrdersBreakdown({ period: { year: y, month: m }, loading: false, data: res.data });
      } catch {
        toast.error("Erreur drill-down commandes");
        setOrdersBreakdown({ period: null, loading: false, data: null });
      }
    }
  };

  // ---- Save fiscal config ----
  const handleSaveFiscalConfig = async (month, day) => {
    try {
      await api.updateAppConfig({ fiscal_year_start_month: month, fiscal_year_start_day: day });
      const fresh = await api.getAppConfig();
      setAppConfig(fresh.data);
      toast.success("Année comptable configurée");
    } catch {
      toast.error("Erreur de sauvegarde");
    }
  };

  // ---- Custom Tooltip ----
  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};
    return (
      <div className="bg-white p-3 border rounded shadow-lg text-xs">
        <p className="font-semibold mb-1">{formatMonthKey(label)}</p>
        {payload.map((entry, i) => {
          const isCount = entry.name?.toLowerCase().includes("nb") || entry.dataKey?.toLowerCase().includes("count");
          return (
            <p key={i} style={{ color: entry.color }}>
              <span className="font-medium">{entry.name}</span> : {isCount ? entry.value : formatCurrency(entry.value || 0)}
            </p>
          );
        })}
        {row.has_import && (row.orders_ghost_amount > 0 || row.ca_ghost_amount > 0) && (
          <div className="border-t mt-1 pt-1 text-amber-700">
            {row.orders_ghost_amount > 0 && (
              <p>↳ Commandes : {formatCurrency(row.orders_real_amount || 0)} réel + {formatCurrency(row.orders_ghost_amount)} importé</p>
            )}
            {row.ca_ghost_amount > 0 && (
              <p>↳ CA : {formatCurrency(row.ca_real_amount || 0)} réel + {formatCurrency(row.ca_ghost_amount)} importé</p>
            )}
          </div>
        )}
        {row.orders_count > 0 && !payload.some((p) => p.dataKey === "orders_count") && (
          <p className="text-muted-foreground mt-1 border-t pt-1">{row.orders_count} chantier{row.orders_count > 1 ? "s" : ""}</p>
        )}
        {row.ca_orders_count > 0 && (
          <p className="text-muted-foreground">{row.ca_orders_count} chantier{row.ca_orders_count > 1 ? "s" : ""} sur CA</p>
        )}
      </div>
    );
  };

  // ---- Stacked-by-type data ----
  const stackedTypeData = useMemo(() => {
    if (!orderTypeBreakdown) return [];
    return (orderTypeData.periods || []).map((p) => ({
      period: p.period,
      ...p.by_type,
    }));
  }, [orderTypeData, orderTypeBreakdown]);

  // ---- Render ----
  return (
    <div className="space-y-6 animate-in" data-testid="analytics-page" id="analytics-content">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Graphiques</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Analyse de l'activité — CA calculé à l'avancement des chantiers.
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="export-pdf">
          <FileDown className="h-4 w-4 mr-2" />
          Imprimer / PDF
        </Button>
      </div>

      <ChartFiltersBar
        timeRange={timeRange} setTimeRange={setTimeRange}
        startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate}
        orderTypes={orderTypes}
        selectedOrderTypeIds={selectedOrderTypeIds}
        setSelectedOrderTypeIds={setSelectedOrderTypeIds}
        sellersList={sellersList}
        selectedSellerIds={selectedSellerIds}
        setSelectedSellerIds={setSelectedSellerIds}
        sellerDisplayMode={sellerDisplayMode}
        setSellerDisplayMode={setSellerDisplayMode}
        appConfig={appConfig}
        onFiscalConfigChange={handleSaveFiscalConfig}
        canEditFiscal={isOrgAdmin || isSuperAdmin}
      />

      {/* Anomalies */}
      {(periodsData.anomalies || []).length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="anomalies-banner">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm text-amber-900">
                  {periodsData.anomalies.length} anomalie{periodsData.anomalies.length > 1 ? "s" : ""} détectée{periodsData.anomalies.length > 1 ? "s" : ""}
                </p>
                <p className="text-xs text-amber-800 mt-0.5 mb-2">
                  Le montant réel dépasse le montant importé. La valeur importée est conservée — vérifie tes imports.
                </p>
                <ul className="space-y-1 text-xs text-amber-900">
                  {periodsData.anomalies.slice(0, 5).map((a, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">{formatMonthKey(a.period)}</span>
                      <span>{a.type === "ca" ? "CA" : "Commandes"} : réel {formatCurrency(a.real_amount)} &gt; import {formatCurrency(a.imported_amount)}</span>
                      <span className="text-amber-700">(écart +{formatCurrency(a.delta)})</span>
                    </li>
                  ))}
                  {periodsData.anomalies.length > 5 && (
                    <li className="text-amber-700 italic">… et {periodsData.anomalies.length - 5} autre{periodsData.anomalies.length - 5 > 1 ? "s" : ""}</li>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      {!loading && periodsData.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
          <SummaryCard label="Commandes" value={formatCurrency(periodsData.summary.orders_total || 0)} sub={`${periodsData.summary.orders_count || 0} chantier${(periodsData.summary.orders_count || 0) > 1 ? "s" : ""}`} color={BRAND_BLUE} />
          <SummaryCard label="CA réalisé" value={formatCurrency(periodsData.summary.ca_total || 0)} sub={`${periodsData.summary.ca_orders_count || 0} chantier${(periodsData.summary.ca_orders_count || 0) > 1 ? "s" : ""}`} color={BRAND_BLUE_DARK} />
          <SummaryCard label="Stock fin de période" value={formatCurrency(periodsData.summary.stock_current || 0)} sub="en attente" color="#0891b2" />
          <SummaryCard label="Stock instantané" value={formatCurrency(stockInstant?.stock_amount || 0)} sub="aujourd'hui" color="#7c3aed" />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="stock" data-testid="tab-stock"><Package className="h-4 w-4 mr-1.5" /> Stock</TabsTrigger>
          <TabsTrigger value="ca" data-testid="tab-ca"><TrendingUp className="h-4 w-4 mr-1.5" /> Chiffre d'affaires</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><BarChart3 className="h-4 w-4 mr-1.5" /> Commandes</TabsTrigger>
          <TabsTrigger value="combined" data-testid="tab-combined"><Layers className="h-4 w-4 mr-1.5" /> Vue combinée</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">Comparaison</TabsTrigger>
          <TabsTrigger value="variance" data-testid="tab-variance"><AlertTriangle className="h-4 w-4 mr-1.5" />Écarts Prévu/Réel</TabsTrigger>
          <TabsTrigger value="imports" data-testid="tab-imports">Imports historiques</TabsTrigger>
        </TabsList>

        {loading && activeTab !== "compare" && activeTab !== "imports" && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* === STOCK === */}
        <TabsContent value="stock">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
              <CardTitle className="font-heading">Évolution du stock de commandes</CardTitle>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Mode :</Label>
                <Switch checked={stockMode === "instant"} onCheckedChange={(v) => setStockMode(v ? "instant" : "monthly")} data-testid="stock-mode-toggle" />
                <span className="text-xs text-muted-foreground">{stockMode === "instant" ? "Instantané" : "Mois par mois"}</span>
              </div>
            </CardHeader>
            <CardContent>
              {stockMode === "instant" ? (
                <div className="text-center py-12" data-testid="stock-instant-value">
                  <p className="text-xs text-muted-foreground mb-2">Stock total à ce jour</p>
                  <p className="text-4xl font-bold text-primary">{formatCurrency(stockInstant?.stock_amount || 0)}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    = {formatCurrency(stockInstant?.total_orders_amount || 0)} commandes − {formatCurrency(stockInstant?.total_ca_amount || 0)} CA
                  </p>
                </div>
              ) : (periodsData.periods || []).length === 0 ? (
                <EmptyState />
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={periodsData.periods}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" tickFormatter={formatMonthKey} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="stock_amount" name="Stock (€)" fill={BRAND_BLUE} radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === CA === */}
        <TabsContent value="ca" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
              <CardTitle className="font-heading">Chiffre d'affaires (à l'avancement)</CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={caCumulative} onCheckedChange={setCaCumulative} data-testid="ca-cumul-toggle" id="ca-cumul" />
                  <Label htmlFor="ca-cumul" className="text-xs">Cumulé</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={orderTypeBreakdown} onCheckedChange={setOrderTypeBreakdown} data-testid="type-breakdown-toggle" id="type-bd" />
                  <Label htmlFor="type-bd" className="text-xs">Ventilation par type</Label>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(periodsData.periods || []).length === 0 ? (
                <EmptyState />
              ) : orderTypeBreakdown && stackedTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={stackedTypeData} onClick={(p) => handleBarClick("ca", p)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" tickFormatter={formatMonthKey} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {(orderTypeData.types || []).map((t, i) => (
                      <Bar key={t.id} dataKey={t.id} name={t.name} stackId="a" fill={colorFor(i)} radius={i === orderTypeData.types.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} cursor="pointer" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={periodsWithCumul} onClick={(p) => handleBarClick("ca", p)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" tickFormatter={formatMonthKey} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {sellerDisplayMode === "distinct" && selectedSellerIds.length > 1 ? (
                      selectedSellerIds.map((sid, i) => {
                        const seller = sellersList.find((s) => s.id === sid);
                        return (
                          <Bar
                            key={sid}
                            dataKey={(row) => row.by_seller_ca?.[sid] || 0}
                            name={seller?.name || sid}
                            fill={colorFor(i)}
                            radius={[4, 4, 0, 0]}
                            cursor="pointer"
                            stackId={caCumulative ? undefined : "ca"}
                          />
                        );
                      })
                    ) : (
                      <Bar dataKey={caCumulative ? "ca_cumul" : "ca_amount"} name={caCumulative ? "CA cumulé (€)" : "CA (€)"} fill={BRAND_BLUE} radius={[4, 4, 0, 0]} cursor="pointer" />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          {caBreakdown.period && (
            <DrillDownOrdersCard
              mode="ca"
              year={caBreakdown.period.year}
              month={caBreakdown.period.month}
              data={caBreakdown.data?.rows ? { orders: caBreakdown.data.rows, total_ca: caBreakdown.data.total_ca, count: caBreakdown.data.count } : caBreakdown.data}
              loading={caBreakdown.loading}
              onClose={() => setCaBreakdown({ period: null, loading: false, data: null })}
            />
          )}
        </TabsContent>

        {/* === ORDERS === */}
        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
              <CardTitle className="font-heading">Commandes</CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={ordersCumulative} onCheckedChange={setOrdersCumulative} data-testid="orders-cumul-toggle" id="orders-cumul" />
                  <Label htmlFor="orders-cumul" className="text-xs">Cumulé</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={orderTypeBreakdown} onCheckedChange={setOrderTypeBreakdown} data-testid="orders-type-breakdown-toggle" id="orders-type-bd" />
                  <Label htmlFor="orders-type-bd" className="text-xs">Ventilation par type</Label>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(periodsData.periods || []).length === 0 ? (
                <EmptyState />
              ) : orderTypeBreakdown && stackedTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={stackedTypeData} onClick={(p) => handleBarClick("orders", p)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" tickFormatter={formatMonthKey} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {(orderTypeData.types || []).map((t, i) => (
                      <Bar key={t.id} dataKey={t.id} name={t.name} stackId="a" fill={colorFor(i)} cursor="pointer" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={periodsWithCumul} onClick={(p) => handleBarClick("orders", p)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" tickFormatter={formatMonthKey} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {sellerDisplayMode === "distinct" && selectedSellerIds.length > 1 ? (
                      selectedSellerIds.map((sid, i) => {
                        const seller = sellersList.find((s) => s.id === sid);
                        return (
                          <Bar
                            key={sid}
                            yAxisId="left"
                            dataKey={(row) => row.by_seller_orders?.[sid]?.amount || 0}
                            name={seller?.name || sid}
                            fill={colorFor(i)}
                            cursor="pointer"
                            stackId={ordersCumulative ? undefined : "orders"}
                          />
                        );
                      })
                    ) : (
                      <>
                        <Bar yAxisId="left" dataKey={ordersCumulative ? "orders_cumul" : "orders_amount"} name={ordersCumulative ? "Cumulé (€)" : "Montant (€)"} fill={BRAND_BLUE} radius={[4, 4, 0, 0]} cursor="pointer" />
                        <Line yAxisId="right" type="monotone" dataKey="orders_count" name="Nb commandes" stroke={BRAND_BLUE_DARK} strokeWidth={2} dot={{ r: 3 }} />
                      </>
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          {ordersBreakdown.period && (
            <DrillDownOrdersCard
              mode="orders"
              year={ordersBreakdown.period.year}
              month={ordersBreakdown.period.month}
              data={ordersBreakdown.data?.rows ? { orders: ordersBreakdown.data.rows, total_amount_ht: ordersBreakdown.data.total_amount_ht, count: ordersBreakdown.data.count } : ordersBreakdown.data}
              loading={ordersBreakdown.loading}
              onClose={() => setOrdersBreakdown({ period: null, loading: false, data: null })}
            />
          )}
        </TabsContent>

        {/* === COMBINED === */}
        <TabsContent value="combined">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Vue combinée — Commandes / CA / Stock</CardTitle>
            </CardHeader>
            <CardContent>
              {(periodsData.periods || []).length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height={450}>
                  <ComposedChart data={periodsData.periods}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" tickFormatter={formatMonthKey} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="orders_amount" name="Commandes (€)" fill={BRAND_BLUE} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="ca_amount" name="CA (€)" fill={BRAND_BLUE_DARK} radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="stock_amount" name="Stock (€)" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === COMPARE === */}
        <TabsContent value="compare" className="space-y-4">
          <AdvancedCompare
            orderTypes={orderTypes}
            sellersList={sellersList}
            persistKey="analytics_advanced_compare"
            height={420}
          />
          <MultiYearCompare orderTypeIds={selectedOrderTypeIds} sellerIds={selectedSellerIds} />
        </TabsContent>

        {/* === VARIANCE PRÉVU/RÉEL === */}
        <TabsContent value="variance">
          <ForecastActualVarianceTab defaultThreshold={appConfig?.forecast_actual_variance_threshold_pct ?? 10} />
        </TabsContent>

        {/* === IMPORTS HISTORIQUES === */}
        <TabsContent value="imports">
          <HistoricalImportsManager onChanged={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const SummaryCard = ({ label, value, sub, color }) => (
  <Card>
    <CardContent className="pt-6">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-xl font-bold" style={{ color }}>{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{sub}</p>
    </CardContent>
  </Card>
);

const EmptyState = () => (
  <div className="text-center py-16 text-muted-foreground text-sm">
    Aucune donnée disponible pour cette période et ces filtres.
  </div>
);

export default AnalyticsPage;
